import { useContext, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Redirect } from 'react-router-dom';
import { FlexContainer, Spinner } from '@taxdown/piccolo-web';
import { getChildIndex } from 'Store/modules/router/selectors';
import { taxSelectedSelector } from 'Store/modules/tax/selectors';
import KamiContext from 'Context/KamiContext';
import { ReadOnlyProvider } from 'Context/validations';
import { useFlags } from 'Hooks/useFlags';
import ContentContainer from 'Components/UI/ContentContainer';
import FlowColLayout from 'Components/Layouts/FlowColLayout';
import Trans from 'Components/Trans';
import { routes } from 'Constants/routes';
import { FlagsKey, ScreenTypes } from 'Types/common';
import { ScreenProps, ValidationsProps } from './types';
import screenByType from './screenByType';
import { getScreenName } from './utils';
import { useFetchGetUserData, useFetchValidation } from './hooks';
import * as Styled from './styles';

const Screen = ({ screenUrl }: ScreenProps): JSX.Element | null => {
  const { kamiSama, initKamiSama } = useContext(KamiContext);
  const tax = useSelector(taxSelectedSelector);
  const { enabled: flowEnabled } = useFlags(FlagsKey.FLOW_ENABLED);
  const childIndex = getChildIndex();
  const screens = kamiSama?.getForm();
  const { data: forms, isLoading } = useFetchValidation();
  const { isLoading: isLoadingUserData, data: userData } = useFetchGetUserData(
    Boolean(tax?.recordId)
  );
  const screenName = getScreenName(kamiSama, screens, screenUrl);

  useEffect(() => {
    if (forms && userData) {
      initKamiSama({ forms, userData });
    }
  }, [forms, userData]);

  if (flowEnabled) {
    return <Redirect to={routes.DASHBOARD} />;
  }

  if (!kamiSama || !screens || isLoadingUserData || isLoading || !screenName || !forms) {
    return (
      <FlowColLayout isFlow>
        <ContentContainer>
          <FlexContainer direction="column" justify="center" align="center" fullHeight>
            <FlexContainer>
              <Spinner size="M" justify="center" align="center" />
            </FlexContainer>
            <Styled.Text type="paragraphs" color="dark" align="center" size="S_S_S">
              <Trans
                i18nKey="Loader.description"
                ns="validations"
                components={{
                  span: <Styled.Span />,
                }}
              />
            </Styled.Text>
          </FlexContainer>
        </ContentContainer>
      </FlowColLayout>
    );
  }

  if (!screenName || (screenName && !kamiSama?.getScreen(screenName)?.isShow())) {
    return <Redirect to={routes.DASHBOARD} />;
  }

  if (
    forms.screenForm.dictionary[screenName].screenType === ScreenTypes.CHILD &&
    (childIndex === null || childIndex === undefined)
  ) {
    return <Redirect to={routes.DASHBOARD} />;
  }

  const { screenType, section } = screens[screenName];

  const ScreenComponent = screenByType[screenType ?? ''] ?? null;

  return (
    <ReadOnlyProvider section={section}>
      <FlowColLayout section={section} isFlow>
        <ScreenComponent screenName={screenName} />
      </FlowColLayout>
    </ReadOnlyProvider>
  );
};

const Validations = ({ match }: ValidationsProps): JSX.Element => <Screen screenUrl={match.url} />;

export default Validations;
